/**
 * AdminConsoleLayout — Ribbon-based navigation for the Admin Console.
 * Uses the standard ModuleLayout + useAdminRibbon pattern, consistent with all other modules.
 * The nesting guard prevents re-rendering when sub-pages are embedded in tab wrappers.
 */

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAdminRibbon } from '../../../hooks/useAdminRibbon';
import type { RibbonAction } from '../../../types/ribbon';
import ModuleLayout from '../../../components/layout/ModuleLayout';
import { AdminConsoleContextBar } from './AdminConsoleContextBar';

/* ─── Nesting Guard ──────────────────────────────────────── */
const AdminLayoutContext = React.createContext(false);

/* ─── Layout ─────────────────────────────────────────────── */
interface AdminConsoleLayoutProps {
  children: React.ReactNode;
  /** Optional ReactNode rendered after ribbon groups (search, filters) */
  ribbonSlots?: { afterGroups?: React.ReactNode };
  /** Extra action buttons rendered as ribbon buttons (icon top, label bottom) */
  contextActions?: RibbonAction[];
}

export const AdminConsoleLayout: React.FC<AdminConsoleLayoutProps> = ({ children, ribbonSlots, contextActions }) => {
  const isNested = React.useContext(AdminLayoutContext);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const handleNavigate = React.useCallback((path: string) => {
    navigate(path);
  }, [navigate]);

  const ribbonConfig = useAdminRibbon({
    onNavigate: handleNavigate,
    currentPath: pathname,
    slots: ribbonSlots,
    contextActions,
  });

  // If already inside an AdminConsoleLayout, just render children (tab context)
  if (isNested) return <>{children}</>;

  return (
    <AdminLayoutContext.Provider value={true}>
      <ModuleLayout ribbonConfig={ribbonConfig}>
        <div className="flex h-full min-h-0 flex-col bg-[var(--bg-app)]">
          <AdminConsoleContextBar pathname={pathname} />
          <div className="min-h-0 flex-1 overflow-y-auto">
            {children}
          </div>
        </div>
      </ModuleLayout>
    </AdminLayoutContext.Provider>
  );
};
